import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  completeGoogleSignup,
  createOAuth,
  login,
  loginGoogle,
  loginOAuth,
  requestOTP,
  verifyOTP,
  createNewPassword,
  changePassword,
  setupTwoFactor,
  verifyTwoFactor,
  initializeDisableTwoFactor,
  disableTwoFactor,
} from './request';
import toast from 'react-hot-toast';
import { USER_QUERY_KEY } from '@/api/queries/user/key';

export const useRequestOTP = (callback?: () => void) => {
  return useMutation({
    mutationFn: requestOTP,
    onSuccess: ({ response }, variables) => {
      const message =
        variables.channel === 'whatsapp'
          ? 'Verification code sent to your WhatsApp and email. Please check both.'
          : response;
      toast.success(message);
      callback?.();
    },
  });
};

export const useLogin = (
  onSuccess?: (data: Awaited<ReturnType<typeof login>>) => void
) => {
  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
  });
};

export const useLoginOAuth = (
  onSuccess?: (data: Awaited<ReturnType<typeof loginOAuth>>) => void
) => {
  return useMutation({
    mutationFn: loginOAuth,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
  });
};

export const useLoginGoogle = (
  onSuccess?: (data: Awaited<ReturnType<typeof loginGoogle>>) => void,
  onError?: (error: Error) => void
) => {
  return useMutation({
    mutationFn: loginGoogle,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      onError?.(error);
    },
  });
};

export const useCompleteGoogleSignup = (
  onSuccess?: (data: Awaited<ReturnType<typeof completeGoogleSignup>>) => void
) => {
  return useMutation({
    mutationFn: completeGoogleSignup,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
  });
};

export const useCreateOAuth = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: createOAuth,
    onSuccess: ({ response }) => {
      toast.success(response);
      onSuccess?.();
    },
  });
};

export const useVerifyOTP = (
  onSuccess?: (data: Awaited<ReturnType<typeof verifyOTP>>) => void
) => {
  return useMutation({
    mutationFn: verifyOTP,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
  });
};

export const useCreateNewPassword = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: createNewPassword,
    onSuccess: ({ response }) => {
      toast.success(response);
      onSuccess?.();
    },
  });
};

export const useChangePassword = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: ({ response }) => {
      toast.success(response);
      onSuccess?.();
    },
  });
};

export const useSetupTwoFactor = (
  onSuccess?: (data: Awaited<ReturnType<typeof setupTwoFactor>>) => void
) => {
  return useMutation({
    mutationFn: setupTwoFactor,
    onSuccess: (data) => {
      toast.success(data.response);
      onSuccess?.(data);
    },
  });
};

export const useVerifyTwoFactor = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyTwoFactor,
    onSuccess: ({ response }) => {
      toast.success(response);
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
      onSuccess?.();
    },
  });
};

export const useInitializeDisableTwoFactor = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: initializeDisableTwoFactor,
    onSuccess: ({ response }) => {
      toast.success(response);
      onSuccess?.();
    },
  });
};

export const useDisableTwoFactor = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disableTwoFactor,
    onSuccess: ({ response }) => {
      toast.success(response);
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
      onSuccess?.();
    },
  });
};
